/**
 * Context helpers + body-fat % formulas for skinfold protocols.
 * Densities use Siri: %BF = 495/D − 450 unless the protocol returns % directly.
 */

import {
  SKINFOLD_PROTOCOLS,
  SKINFOLD_SITES,
  formatMm,
  getFilledSiteKeys,
  getSiteMeanMap,
  isMalePatient,
  isProtocolCompatible,
  protocolsFromMeasurement,
  sitesStateFromMeasurement,
  sumSiteMeans,
} from "./skinfold-utils";

const round1 = (value) => Math.round(value * 10) / 10;

const siriPercent = (density) => {
  if (density == null || density <= 0) {
    return null;
  }
  return round1(495 / density - 450);
};

const parseDisplayDate = (value) => {
  if (!value) {
    return null;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  const raw = String(value).trim();
  const br = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (br) {
    return new Date(Number(br[3]), Number(br[2]) - 1, Number(br[1]));
  }
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getPatientSexCode = (patient) => {
  const gender = patient?.gender || patient?.account?.gender;
  if (gender === "MALE" || gender === "Masculino") {
    return "MALE";
  }
  if (gender === "FEMALE" || gender === "Feminino") {
    return "FEMALE";
  }
  return null;
};

export const getPatientSexLabel = (patient) => {
  const code = getPatientSexCode(patient);
  if (code === "MALE") {
    return "Masculino";
  }
  if (code === "FEMALE") {
    return "Feminino";
  }
  return "—";
};

export const getPatientAgeYears = (patient, visitDate) => {
  const reference = parseDisplayDate(visitDate) || new Date();
  const birthRaw =
    patient?.birth_date || patient?.account?.birth_date || patient?.birthDate;
  const birth = parseDisplayDate(birthRaw);
  if (birth) {
    let age = reference.getFullYear() - birth.getFullYear();
    const monthDiff = reference.getMonth() - birth.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && reference.getDate() < birth.getDate())
    ) {
      age -= 1;
    }
    return age >= 0 && age < 130 ? age : null;
  }
  const fallback = patient?.body_measurement?.age;
  const number = Number(fallback);
  return Number.isFinite(number) && number > 0 ? number : null;
};

export const getWeightKg = (previousMeasurement, patient) => {
  const fromMeasurement = previousMeasurement?.metabolic_data?.weight_kg;
  if (fromMeasurement != null && Number.isFinite(Number(fromMeasurement))) {
    return Number(fromMeasurement);
  }
  const fromPatient = patient?.body_measurement?.weight;
  if (fromPatient != null && Number.isFinite(Number(fromPatient))) {
    return Number(fromPatient);
  }
  return null;
};

export const getHeightCm = (previousMeasurement, patient) => {
  const fromMeasurement = previousMeasurement?.metabolic_data?.height_cm;
  if (fromMeasurement != null && Number.isFinite(Number(fromMeasurement))) {
    return Number(fromMeasurement);
  }
  const fromPatient = patient?.body_measurement?.height;
  if (fromPatient != null && Number.isFinite(Number(fromPatient))) {
    return Number(fromPatient);
  }
  return null;
};

export const buildCalculationContext = ({
  patient,
  previousMeasurement,
  visitDate,
}) => {
  const sex = getPatientSexCode(patient);
  const age = getPatientAgeYears(patient, visitDate);
  const weightKg = getWeightKg(previousMeasurement, patient);
  const heightCm = getHeightCm(previousMeasurement, patient);
  const missing = [];
  if (!sex) {
    missing.push("sexo");
  }
  if (age == null) {
    missing.push("idade (data de nascimento)");
  }
  if (weightKg == null) {
    missing.push("peso");
  }
  return {
    sex,
    sexLabel: getPatientSexLabel(patient),
    age,
    weightKg,
    heightCm,
    male: sex === "MALE",
    missing,
    canCalculatePercent: Boolean(sex && age != null),
    canCalculateComposition: Boolean(sex && age != null && weightKg != null),
  };
};

const jp3Sites = (male) =>
  male ? ["chest", "abdominal", "thigh"] : ["triceps", "suprailiac", "thigh"];

const jp7Sites = [
  "chest",
  "midaxillary",
  "triceps",
  "subscapular",
  "abdominal",
  "suprailiac",
  "thigh",
];

const jp6Sites = [
  "chest",
  "midaxillary",
  "triceps",
  "subscapular",
  "abdominal",
  "thigh",
];

const protocol6Sites = [
  "triceps",
  "subscapular",
  "suprailiac",
  "abdominal",
  "thigh",
  "medial_calf",
];

const durninSites = ["biceps", "triceps", "subscapular", "suprailiac"];
const faulknerSites = ["triceps", "subscapular", "suprailiac", "abdominal"];
const petroskiSites = ["triceps", "subscapular", "suprailiac", "midaxillary"];
const guedesSites = ["triceps", "subscapular", "suprailiac"];

const densityJacksonPollock3 = (sum, age, male) => {
  if (male) {
    return (
      1.10938 -
      0.0008267 * sum +
      0.0000016 * sum * sum -
      0.0002574 * age
    );
  }
  return (
    1.0994921 -
    0.0009929 * sum +
    0.0000023 * sum * sum -
    0.0001392 * age
  );
};

const densityJacksonPollock7 = (sum, age, male) => {
  if (male) {
    return (
      1.112 -
      0.00043499 * sum +
      0.00000055 * sum * sum -
      0.00028826 * age
    );
  }
  return (
    1.097 -
    0.00046971 * sum +
    0.00000056 * sum * sum -
    0.00012828 * age
  );
};

/** Durnin & Womersley 1974 — L = log10(Σ4). */
const densityDurninWomersley = (sum4, age, male) => {
  if (sum4 <= 0) {
    return null;
  }
  const L = Math.log10(sum4);
  if (male) {
    if (age < 20) {
      return 1.1620 - 0.0630 * L;
    }
    if (age < 30) {
      return 1.1631 - 0.0632 * L;
    }
    if (age < 40) {
      return 1.1422 - 0.0544 * L;
    }
    if (age < 50) {
      return 1.1620 - 0.07 * L;
    }
    return 1.1715 - 0.0779 * L;
  }
  if (age < 20) {
    return 1.1549 - 0.0678 * L;
  }
  if (age < 30) {
    return 1.1599 - 0.0717 * L;
  }
  if (age < 40) {
    return 1.1423 - 0.0632 * L;
  }
  if (age < 50) {
    return 1.1333 - 0.0612 * L;
  }
  return 1.1339 - 0.0645 * L;
};

const densityPetroski = (sum4, age, male) => {
  if (male) {
    return (
      1.10726863 -
      0.00081201 * sum4 +
      0.00000212 * sum4 * sum4 -
      0.00041761 * age
    );
  }
  if (sum4 <= 0) {
    return null;
  }
  return 1.1954553 - 0.07513507 * Math.log10(sum4) - 0.00041072 * age;
};

const densityGuedes = (sum3, male) => {
  if (sum3 <= 0) {
    return null;
  }
  const L = Math.log10(sum3);
  return male ? 1.1714 - 0.0671 * L : 1.1665 - 0.0706 * L;
};

const slaughterPercent = (meanMap, male) => {
  const triceps = meanMap.triceps;
  const subscapular = meanMap.subscapular;
  const calf = meanMap.medial_calf;

  if (triceps != null && subscapular != null) {
    const sum = triceps + subscapular;
    if (male) {
      return round1(1.21 * sum - 0.008 * sum * sum - 1.7);
    }
    return round1(1.33 * sum - 0.013 * sum * sum - 2.5);
  }

  if (triceps != null && calf != null) {
    const sum = triceps + calf;
    return male ? round1(0.735 * sum + 1.0) : round1(0.61 * sum + 5.1);
  }

  return null;
};

export const calculateProtocolBodyFat = ({
  protocolId,
  sitesState,
  patient,
  age,
  male,
}) => {
  if (age == null || male == null) {
    return { percent: null, note: null };
  }

  const meanMap = getSiteMeanMap(sitesState);
  let percent = null;
  let note = null;

  switch (protocolId) {
    case "JACKSON_POLLOCK_3": {
      const sum = sumSiteMeans(meanMap, jp3Sites(male));
      percent = sum != null ? siriPercent(densityJacksonPollock3(sum, age, male)) : null;
      break;
    }
    case "JACKSON_POLLOCK_7": {
      const sum = sumSiteMeans(meanMap, jp7Sites);
      percent = sum != null ? siriPercent(densityJacksonPollock7(sum, age, male)) : null;
      break;
    }
    case "JACKSON_POLLOCK_6": {
      const sum = sumSiteMeans(meanMap, jp6Sites);
      percent = sum != null ? siriPercent(densityJacksonPollock7(sum, age, male)) : null;
      break;
    }
    case "PROTOCOL_6_SKINFOLDS": {
      const sum = sumSiteMeans(meanMap, protocol6Sites);
      percent = sum != null ? siriPercent(densityJacksonPollock7(sum, age, male)) : null;
      break;
    }
    case "DURNIN_WOMERSLEY": {
      const sum = sumSiteMeans(meanMap, durninSites);
      percent =
        sum != null ? siriPercent(densityDurninWomersley(sum, age, male)) : null;
      break;
    }
    case "FAULKNER": {
      const sum = sumSiteMeans(meanMap, faulknerSites);
      percent = sum != null ? round1(5.783 + 0.153 * sum) : null;
      break;
    }
    case "PETROSKI": {
      const sum = sumSiteMeans(meanMap, petroskiSites);
      percent = sum != null ? siriPercent(densityPetroski(sum, age, male)) : null;
      break;
    }
    case "GUEDES": {
      const sum = sumSiteMeans(meanMap, guedesSites);
      percent = sum != null ? siriPercent(densityGuedes(sum, male)) : null;
      break;
    }
    case "SLAUGHTER": {
      percent = slaughterPercent(meanMap, male);
      if (age >= 18) {
        note = "Indicada para jovens; use com cautela em adultos.";
      }
      break;
    }
    default:
      break;
  }

  if (percent != null && (percent < 0 || percent > 75)) {
    return { percent: null, note: "Resultado fora da faixa esperada." };
  }

  return { percent, note };
};

export const calculateBodyComposition = (weightKg, bodyFatPercent) => {
  if (weightKg == null || bodyFatPercent == null) {
    return { fatMassKg: null, leanMassKg: null };
  }
  const fatMassKg = round1(weightKg * (bodyFatPercent / 100));
  const leanMassKg = round1(weightKg - fatMassKg);
  return { fatMassKg, leanMassKg };
};

export const formatPercent = (value) => {
  if (value == null) {
    return "—";
  }
  return `${String(value).replace(".", ",")} %`;
};

export const formatKg = (value) => {
  if (value == null) {
    return "—";
  }
  return `${String(value).replace(".", ",")} kg`;
};

/**
 * Rows for selected compatible protocols + primary composition.
 */
export const buildBodyFatResults = ({
  sitesState,
  selectedProtocols = [],
  patient,
  context,
  filledKeys,
}) => {
  if (!context?.canCalculatePercent) {
    return {
      rows: [],
      primary: null,
      composition: { fatMassKg: null, leanMassKg: null },
    };
  }

  const male = context.male;
  const age = context.age;
  const rows = [];

  selectedProtocols.forEach((protocolId) => {
    const protocol = SKINFOLD_PROTOCOLS.find((item) => item.id === protocolId);
    if (!protocol) {
      return;
    }
    if (!isProtocolCompatible(protocol, filledKeys, patient)) {
      return;
    }
    const { percent, note } = calculateProtocolBodyFat({
      protocolId,
      sitesState,
      patient,
      age,
      male,
    });
    rows.push({
      id: protocolId,
      label: protocol.label,
      percent,
      note,
    });
  });

  const primary = rows.find((row) => row.percent != null) || rows[0] || null;
  const composition = calculateBodyComposition(
    context.weightKg,
    primary?.percent ?? null
  );

  return { rows, primary, composition };
};

/**
 * Restores the composition reference from persisted body fat %, falling back to list order.
 */
export const resolveCompositionProtocolId = ({
  measurement,
  sitesState,
  selectedProtocols = [],
  patient,
  context,
  filledKeys,
}) => {
  if (!selectedProtocols.length) {
    return null;
  }

  const storedPercent = measurement?.anthropometry?.body_fat_percentage;
  if (storedPercent != null && context?.canCalculatePercent) {
    const tolerance = 0.15;
    for (const protocolId of selectedProtocols) {
      const protocol = SKINFOLD_PROTOCOLS.find((item) => item.id === protocolId);
      if (!protocol || !isProtocolCompatible(protocol, filledKeys, patient)) {
        continue;
      }
      const { percent } = calculateProtocolBodyFat({
        protocolId,
        sitesState,
        patient,
        age: context.age,
        male: context.male,
      });
      if (percent != null && Math.abs(percent - storedPercent) <= tolerance) {
        return protocolId;
      }
    }
  }

  return selectedProtocols[0];
};

/**
 * Hub preview: filled site means + body-fat % for each selected protocol.
 */
export const buildSkinfoldHubItems = (measurement, patient) => {
  if (!measurement) {
    return { sites: [], protocols: [] };
  }

  const sitesState = sitesStateFromMeasurement(measurement);
  const filledKeys = getFilledSiteKeys(sitesState);
  const selectedProtocols = protocolsFromMeasurement(measurement);

  const sites = SKINFOLD_SITES.filter((site) => filledKeys.includes(site.key)).map(
    (site) => {
      const state = sitesState.find((item) => item.key === site.key);
      return {
        key: site.key,
        title: site.label,
        fullTitle: site.label,
        value: formatMm(state?.mean),
      };
    }
  );

  const visitDate = measurement.visit_date || measurement.visitDate;
  const context = buildCalculationContext({
    patient,
    previousMeasurement: measurement,
    visitDate,
  });

  const protocols = selectedProtocols
    .map((protocolId) => {
      const protocol = SKINFOLD_PROTOCOLS.find((item) => item.id === protocolId);
      if (!protocol) {
        return null;
      }

      let value = "—";
      if (
        context.canCalculatePercent &&
        isProtocolCompatible(protocol, filledKeys, patient)
      ) {
        const { percent } = calculateProtocolBodyFat({
          protocolId,
          sitesState,
          patient,
          age: context.age,
          male: context.male,
        });
        value = formatPercent(percent);
      }

      return {
        key: protocolId,
        title: protocol.label,
        fullTitle: protocol.label,
        value,
      };
    })
    .filter(Boolean);

  return { sites, protocols };
};
